import React, { useState, useEffect, useMemo, useCallback } from 'react';
import SeatGrid from '../components/SeatGrid';
import PersonManagementPanel from '../components/PersonManagementPanel';
import StatsDisplay from '../components/StatsDisplay';
import TopHeader from '../components/layout/TopHeader';
import { Person, SeatPosition, Version } from '../types';
import { useEditMode } from '../contexts/EditModeContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const DEPARTMENT_OPTIONS = [
  { value: 'pxd', label: 'pxd', color: 'rgba(128,128,128,0.3)' },
  { value: '프리랜서', label: '프리랜서', color: 'rgba(0,123,255,0.3)' },
  { value: '외주사', label: '외주사', color: 'rgba(0,123,255,0.3)' },
  { value: '계약직', label: '계약직', color: 'rgba(0,123,255,0.3)' },
  { value: '인턴', label: '인턴', color: 'rgba(255,153,0,0.3)' },
  { value: '레이블', label: '레이블', color: 'rgba(68,68,68,1)' },
];

function getColorByDepartment(department: string) {
  return DEPARTMENT_OPTIONS.find(opt => opt.value === department)?.color || DEPARTMENT_OPTIONS[0].color;
}

function getDepartmentColorClass(department: string) {
  if (department === 'pxd') return 'dept-bg-pxd';
  if (department === '인턴') return 'dept-bg-intern';
  if (department === '레이블') return 'dept-bg-label';
  return 'dept-bg-ext';
}

// 위치 충돌을 피하는 빈 위치 찾기 함수 (상단부터 찾기)  
function findAvailablePosition(positions: SeatPosition[], startX: number = 2, startY: number = 4) {
  return { x: 2, y: 4 };
}

// 인원 수정 다이얼로그용 드롭다운 컴포넌트
const EditDialogDepartmentDropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setIsOpen(false);
  };

  return (
    <div ref={dropdownRef} className="custom-dropdown">
      <div 
        className={`form-input dept-cell ${getDepartmentColorClass(value)}`}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{value}</span>
        <i className={`ri-arrow-down-s-line ${isOpen ? 'rotate-180' : ''}`}></i>
      </div>
      
      {isOpen && (
        <div className="dropdown-menu dropdown-down">
          {DEPARTMENT_OPTIONS.map(option => (
            <div
              key={option.value}
              className={`dropdown-item ${value === option.value ? 'selected' : ''}`}
              onClick={() => handleSelect(option.value)}
            >
              <span className={`dept-indicator ${getDepartmentColorClass(option.value)}`}></span>
              <span>{option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SeatArrangementPage: React.FC = () => {
  const [persons, setPersons] = useState<Person[]>([]);
  const [positions, setPositions] = useState<SeatPosition[]>([]);
  const [versions, setVersions] = useState<Version[]>([]);
  const [currentVersion, setCurrentVersion] = useState<Version | null>(null);
  const { isEditMode, setIsEditMode } = useEditMode();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [editPerson, setEditPerson] = useState<Person | null>(null);
  const [deleteVersionId, setDeleteVersionId] = useState<number | null>(null);
  const [showFavoriteInput, setShowFavoriteInput] = useState(false);
  const [favoriteAlias, setFavoriteAlias] = useState('');
  const [selectedVersions, setSelectedVersions] = useState<number[]>([]);
  const [showVersionDropdown, setShowVersionDropdown] = useState(false);
  const [legendFilters, setLegendFilters] = useState({
    pxd: true,
    external: true,
    intern: true,
    planned: true
  });
  const [newlyAddedPersonIds, setNewlyAddedPersonIds] = useState<number[]>([]);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);

  useEffect(() => {
    loadData(undefined, true); // 초기 로딩 시 데이터 불일치 감지 활성화
  }, []);

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (editPerson) {
          setEditPerson(null);
        }
        if (deleteVersionId) {
          setDeleteVersionId(null);
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [editPerson, deleteVersionId]);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    if (!showVersionDropdown) return;
    
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.version-dropdown')) {
        setShowVersionDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showVersionDropdown]);

  // 수정 모드에서 페이지 이탈 시 경고
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isEditMode) {
        event.preventDefault();
        event.returnValue = '';
        return '수정 중인 내용이 저장되지 않습니다. 정말 페이지를 떠나시겠습니까?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isEditMode]);

  const loadData = async (versionId?: number, checkDataIntegrity = false) => {
    // Load persons
    const personsRes = await fetch(`${API_URL}/persons`);
    const personsData = await personsRes.json();
    const processedPersons = personsData.map((person: any) => ({
      ...person,
      isPlanned: Boolean(person.isPlanned)
    }));
    setPersons(processedPersons);

    // Load versions
    const versionsRes = await fetch(`${API_URL}/versions`);
    const versionsData = await versionsRes.json();
    setVersions(versionsData);
    
    // Load positions for current version
    let targetVersion = null;
    if (versionId) {
      targetVersion = versionsData.find((v: Version) => v.id === versionId);
    } else {
      targetVersion = versionsData.find((v: Version) => v.isCurrent);
    }
    
    if (targetVersion) {
      setCurrentVersion(targetVersion);
      const positionsRes = await fetch(`${API_URL}/positions/${targetVersion.id}`);
      const positionsData = await positionsRes.json();
      setPositions(positionsData);
      
      // 페이지 초기 로딩 시 데이터 불일치 자동 해결
      if (checkDataIntegrity) {
        const unmappedPersons = processedPersons.filter((person: Person) => 
          !positionsData.some((pos: SeatPosition) => pos.personId === person.id)
        );
        
        if (unmappedPersons.length > 0) {
          // 배치되지 않은 인원들 자동 삭제
          Promise.all(
            unmappedPersons.map((person: Person) =>
              fetch(`${API_URL}/persons/${person.id}`, { method: 'DELETE' })
            )
          ).then(() => {
            // 삭제 후 persons 데이터만 다시 로드
            fetch(`${API_URL}/persons`)
              .then(res => res.json())
              .then(personsData => {
                const processedPersons = personsData.map((person: any) => ({
                  ...person,
                  isPlanned: Boolean(person.isPlanned)
                }));
                setPersons(processedPersons);
              });
          }).catch(error => {
            console.error('Error deleting unmapped persons:', error);
          });
        }
        
        // 상태 초기화
        setNewlyAddedPersonIds([]);
        setShowUnsavedChangesDialog(false);
      }
    } else {
      setCurrentVersion(null);
      setPositions([]);
    }
  };

  const handleUpdatePerson = async (person: Person) => {
    try {
      const response = await fetch(`${API_URL}/persons/${person.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(person),
      });
      
      if (response.ok) {
        // persons 데이터만 업데이트 (전체 로드 방지)
        const personsRes = await fetch(`${API_URL}/persons`);
        const personsData = await personsRes.json();
        const processedPersons = personsData.map((person: any) => ({
          ...person,
          isPlanned: Boolean(person.isPlanned)
        }));
        setPersons(processedPersons);
        setEditPerson(null);
      }
    } catch (error) {
      console.error('Error updating person:', error);
    }
  };

  const handleAddPerson = async (person: Omit<Person, 'id'>) => {
    try {
      // 수정 모드로 자동 진입
      setIsEditMode(true);
      
      const res = await fetch(`${API_URL}/persons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(person),
      });
      const { id: newId } = await res.json();
      
      // 새로 추가된 인원 ID 추적
      setNewlyAddedPersonIds(prev => [...prev, newId]);

      // persons 데이터만 업데이트 (전체 로드 방지)
      const personsRes = await fetch(`${API_URL}/persons`);
      const personsData = await personsRes.json();
      const processedPersons = personsData.map((person: any) => ({
        ...person,
        isPlanned: Boolean(person.isPlanned)
      }));
      setPersons(processedPersons);

      // 새로운 인원의 위치를 로컬 상태에만 추가 (수정 모드이므로 서버에는 저장하지 않음)
      if (currentVersion) {
        // 2단계 접근: 먼저 GridLayout이 원하는 곳에 배치하게 한 다음, 강제로 원하는 위치로 이동
        const tempPosition: SeatPosition = {
          id: Date.now(), // 임시 ID
          personId: newId,
          x: 0, // GridLayout이 배치할 수 있는 임시 위치
          y: 90, // 하단 임시 위치
          version: currentVersion.id,
        };

        // positions 상태에 새로운 위치 추가
        setPositions(prev => {
          const updated = [...prev, tempPosition];
          
          // 300ms 후 원하는 위치로 강제 이동
          setTimeout(() => {
            // 실제 원하는 위치 찾기
            const targetPosition = findAvailablePosition(updated.slice(0, -1), 2); // 새 인원 제외하고 검색
            
            setPositions(current => {
              const newUpdated = current.map(pos => 
                pos.personId === newId 
                  ? { ...pos, x: targetPosition.x, y: targetPosition.y }
                  : pos
              );
              return newUpdated;
            });
          }, 300);
          
          return updated;
        });
      }
    } catch (error) {
      console.error('Error adding person:', error);
    }
  };

  const handleDeletePerson = async (id: number) => {
    try {
      await fetch(`${API_URL}/persons/${id}`, { method: 'DELETE' });
      
      // persons 데이터만 업데이트 (전체 로드 방지)
      const personsRes = await fetch(`${API_URL}/persons`);
      const personsData = await personsRes.json();
      const processedPersons = personsData.map((person: any) => ({
        ...person,
        isPlanned: Boolean(person.isPlanned)
      }));
      setPersons(processedPersons);

      // 삭제된 인원의 위치도 로컬 상태에서 제거
      setPositions(prev => prev.filter(pos => pos.personId !== id));
    } catch (error) {
      console.error('Error deleting person:', error);
    }
  };

  const handlePositionChange = useCallback((newPositions: SeatPosition[]) => {
    setPositions(newPositions);
  }, []);

  const handleSaveVersion = async () => {
    try {
      // 새 버전 생성
      const versionRes = await fetch(`${API_URL}/versions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `Version ${versions.length + 1}`,
          createdAt: new Date().toISOString(),
        }),
      });
      
      if (!versionRes.ok) {
        console.error('Failed to create new version');
        return;
      }
      
      const { id: newVersionId } = await versionRes.json();

      // 좌석 배치를 새 버전에 저장
      const positionsRes = await fetch(`${API_URL}/positions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          positions: positions.map(({ personId, x, y }) => ({ personId, x, y })),
          version: newVersionId,
        }),
      });

      if (!positionsRes.ok) {
        console.error('Failed to save positions');
        return;
      }

      // 새로 생성된 버전을 현재 버전으로 설정
      await fetch(`${API_URL}/versions/${newVersionId}/current`, {
        method: 'PUT',
      });

      setIsEditMode(false);
      setNewlyAddedPersonIds([]); // 저장 시 새로 추가된 인원 목록 초기화
      loadData(newVersionId);
    } catch (error) {
      console.error('Error saving version:', error);
    }
  };

  // 메모이제이션된 계산 값들
  const filteredPositions = useMemo(() => {
    return positions
      .filter((position, index, self) => 
        self.findIndex(p => p.personId === position.personId) === index
      )
      .filter(position => {
        const person = persons.find(p => p.id === position.personId);
        if (!person) return false;

        if (!legendFilters.pxd && person.department === 'pxd') return false;
        if (!legendFilters.external && ['프리랜서', '외주사', '계약직', '레이블'].includes(person.department)) return false;
        if (!legendFilters.intern && person.department === '인턴') return false;
        if (!legendFilters.planned && person.isPlanned) return false;

        return true;
      });
  }, [positions, persons, legendFilters]);

  const handleVersionChange = (e: any) => {
    const versionId = parseInt(e.target.value);
    const version = versions.find(v => v.id === versionId);
    if (version) {
      fetch(`${API_URL}/versions/${versionId}/current`, {
        method: 'PUT',
      }).then(() => {
        loadData(versionId);
      });
    }
  };

  const handleDeletePersonPanel = async (id: number) => {
    await handleDeletePerson(id);
  };

  const handleDeleteVersion = () => {
    if (!currentVersion) return;
    setDeleteVersionId(currentVersion.id);
  };

  const handleConfirmDeleteVersion = async () => {
    if (!deleteVersionId) return;
    
    try {
      await fetch(`${API_URL}/versions/${deleteVersionId}`, { method: 'DELETE' });
      
      const remainingVersions = versions.filter(v => v.id !== deleteVersionId);
      if (remainingVersions.length > 0) {
        const newCurrentVersion = remainingVersions[0];
        await fetch(`${API_URL}/versions/${newCurrentVersion.id}/current`, {
          method: 'PUT',
        });
        setCurrentVersion(newCurrentVersion);
      } else {
        setCurrentVersion(null);
        setPositions([]);
      }
      
      setDeleteVersionId(null);
      loadData(undefined, true);
    } catch (error) {
      console.error('Error deleting version:', error);
      setDeleteVersionId(null);
    }
  };

  const formatVersionDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const getVersionDisplayText = (version: Version) => {
    const dateTime = formatVersionDate(version.createdAt);
    if (version.isFavorite && version.alias) {
      return `⭐ ${dateTime} - ${version.alias}`;
    }
    return dateTime;
  };

  const handleDuplicatePerson = useCallback(async (person: Person) => {
    try {
      // 수정 모드로 자동 진입
      setIsEditMode(true);
      
      const newPerson = {
        ...person,
        name: `${person.name} 복사`,
      };
      delete (newPerson as any).id;
      
      const res = await fetch(`${API_URL}/persons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPerson),
      });
      const { id: newId } = await res.json();
      
      // 새로 추가된 인원 ID 추적 (복제)
      setNewlyAddedPersonIds(prev => [...prev, newId]);

      // persons 데이터만 업데이트 (전체 로드 방지)
      const personsRes = await fetch(`${API_URL}/persons`);
      const personsData = await personsRes.json();
      const processedPersons = personsData.map((person: any) => ({
        ...person,
        isPlanned: Boolean(person.isPlanned)
      }));
      setPersons(processedPersons);

      // 새로운 인원의 위치를 로컬 상태에만 추가 (수정 모드이므로 서버에는 저장하지 않음)
      if (currentVersion) {
        // 복제된 인원도 2단계 접근: 먼저 임시 위치에 배치 후 원하는 위치로 이동
        const tempPosition: SeatPosition = {
          id: Date.now(),
          personId: newId,
          x: 0, // GridLayout이 배치할 수 있는 임시 위치
          y: 90, // 하단 임시 위치
          version: currentVersion.id
        };
        
        setPositions(prev => {
          const updated = [...prev, tempPosition];
          
          // 300ms 후 원하는 위치로 강제 이동
          setTimeout(() => {
            // 실제 원하는 위치 찾기
            const targetPosition = findAvailablePosition(updated.slice(0, -1), 2); // 새 인원 제외하고 검색
            
            setPositions(current => {
              const newUpdated = current.map(pos => 
                pos.personId === newId 
                  ? { ...pos, x: targetPosition.x, y: targetPosition.y }
                  : pos
              );
              return newUpdated;
            });
          }, 300);
          
          return updated;
        });
      }
      
    } catch (error) {
      console.error('Error duplicating person:', error);
    }
  }, [currentVersion, positions]);

  const handleAddFavorite = async () => {
    if (!currentVersion || !favoriteAlias.trim()) return;

    try {
      await fetch(`${API_URL}/versions/${currentVersion.id}/favorite`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          isFavorite: true,
          alias: favoriteAlias.trim(),
        }),
      });

      loadData(currentVersion.id);
      setShowFavoriteInput(false);
      setFavoriteAlias('');
    } catch (error) {
      console.error('Error adding favorite:', error);
    }
  };

  const handleRemoveFavorite = async () => {
    if (!currentVersion) return;

    try {
      await fetch(`${API_URL}/versions/${currentVersion.id}/favorite`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          isFavorite: false,
          alias: null,
        }),
      });

      loadData(currentVersion.id);
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  const handleVersionSelect = (versionId: number) => {
    setSelectedVersions(prev => 
      prev.includes(versionId)
        ? prev.filter(id => id !== versionId)
        : [...prev, versionId]
    );
  };

  const handleBulkDeleteVersions = async () => {
    if (selectedVersions.length === 0) return;

    const confirmed = window.confirm(`선택한 ${selectedVersions.length}개의 버전을 삭제하시겠습니까?`);
    if (!confirmed) return;

    try {
      await Promise.all(
        selectedVersions.map(versionId =>
          fetch(`${API_URL}/versions/${versionId}`, { method: 'DELETE' })
        )
      );

      if (currentVersion && selectedVersions.includes(currentVersion.id)) {
        const remainingVersions = versions.filter(v => !selectedVersions.includes(v.id));
        if (remainingVersions.length > 0) {
          const newCurrentVersion = remainingVersions[0];
          await fetch(`${API_URL}/versions/${newCurrentVersion.id}/current`, {
            method: 'PUT',
          });
        }
      }

      loadData(undefined, true);
      setSelectedVersions([]);
    } catch (error) {
      console.error('Error deleting versions:', error);
    }
  };

  const handleLegendToggle = useCallback((filterType: keyof typeof legendFilters) => {
    setLegendFilters(prev => ({
      ...prev,
      [filterType]: !prev[filterType]
    }));
  }, []);

  // 수정모드 취소 시 새로 추가된 인원 확인
  const handleEditModeCancel = useCallback(() => {
    if (newlyAddedPersonIds.length > 0) {
      setShowUnsavedChangesDialog(true);
    } else {
      setIsEditMode(false);
      setNewlyAddedPersonIds([]);
    }
  }, [newlyAddedPersonIds, setIsEditMode]);

  // 새로 추가된 인원 삭제하고 나가기
  const handleDeleteAndExit = useCallback(async () => {
    try {
      // 새로 추가된 인원들 삭제
      await Promise.all(
        newlyAddedPersonIds.map(personId =>
          fetch(`${API_URL}/persons/${personId}`, { method: 'DELETE' })
        )
      );
      
      // 데이터 새로고침
      await loadData(currentVersion?.id);
      
      // 수정모드 종료
      setIsEditMode(false);
      setNewlyAddedPersonIds([]);
      setShowUnsavedChangesDialog(false);
    } catch (error) {
      console.error('Error deleting newly added persons:', error);
    }
  }, [newlyAddedPersonIds, currentVersion, loadData, setIsEditMode]);

  // 저장하고 나가기
  const handleSaveAndExit = useCallback(async () => {
    await handleSaveVersion();
    setNewlyAddedPersonIds([]);
    setShowUnsavedChangesDialog(false);
  }, [handleSaveVersion]);

  return (
    <>
      <style>
        {`
          .seat-arrangement-container {
            position: relative;
            min-height: 100vh;
          }
          
          .main-content {
            position: relative;
          }
          
          .edit-dialog-overlay {
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }
          
          .edit-dialog {
            background: white;
            border-radius: 8px;
            min-width: 300px;
            max-width: 400px;
            box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
          }
          
          .edit-dialog-title {
            font-size: 18px;
            font-weight: 600;
            margin: 0 0 16px 0;
            color: #1f2937;
          }
          
          .edit-dialog-message {
            margin: 0 0 20px 0;
            font-size: 14px;
            line-height: 1.5;
            color: #6b7280;
            padding: 0 24px;
          }
          
          .edit-dialog-content {
            display: grid;
            gap: 16px;
          }
          
          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
          }
          
          .form-field {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
          
          .form-label {
            font-size: 14px;
            font-weight: 500;
            color: #374151;
          }
          
          .form-input {
            padding: 8px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
            color: #1f2937;
          }
          
          .form-input:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }
          
          .edit-dialog-actions {
            display: flex;
            gap: 8px;
            justify-content: flex-end;
            margin-top: 8px;
            flex-direction: column;
          }
          
          .dialog-btn {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 8px 22px;
            border: none;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            justify-content: center;
          }
          
          .dialog-btn-save {
            background: #3b82f6;
            color: white;
          }
          
          .dialog-btn-save:hover {
            background: #2563eb;
          }
          
          .dialog-btn-cancel {
            background: #f3f4f6;
            color: #374151;
            border: 1px solid #d1d5db;
          }
          
          .dialog-btn-cancel:hover {
            background: #e5e7eb;
          }
          
          .dialog-btn-delete {
            background: #ef4444;
            color: white;
          }
          
          .dialog-btn-delete:hover {
            background: #dc2626;
          }
          
          .filter-chip {
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 6px 12px;
            background: #f3f4f6;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
            font-size: 12px;
            transition: all 0.2s;
            cursor: pointer;
          }
          
          .filter-chip.active {
            background: #dbeafe;
            border-color: #3b82f6;
            color: #1d4ed8;
          }
          
          .filter-chip i.checked {
            color: #10b981;
          }
          
          .filter-chip i.unchecked {
            color: #d1d5db;
          }
          
          .custom-dropdown {
            position: relative;
          }
          
          .dept-cell {
            cursor: pointer !important;
          }
          
          .dropdown-menu {
            position: absolute;
            top: 100%;
            left: 0;
            right: 0;
            background: white;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
            z-index: 1000;
            max-height: 200px;
            overflow-y: auto;
          }
          
          .dropdown-item {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 12px;
            cursor: pointer;
            font-size: 14px;
            color: #374151;
          }
          
          .dropdown-item:hover {
            background: #f3f4f6;
          }
          
          .dropdown-item.selected {
            background: #dbeafe;
            color: #1d4ed8;
          }
          
          .dept-indicator {
            width: 12px;
            height: 12px;
            border-radius: 2px;
          }
          
          .dept-bg-pxd { background: rgba(128,128,128,0.3); }
          .dept-bg-ext { background: rgba(0,123,255,0.3); }
          .dept-bg-intern { background: rgba(255,153,0,0.3); }
          .dept-bg-label { background: rgba(68,68,68,1); }
          
          .date-input-wrapper {
            position: relative;
          }
          
          .date-input-expanded {
            width: 100%;
          }
        `}
      </style>
    
      <div className="seat-arrangement-container">
        {/* 상단 헤더 */}
        <TopHeader
        legendFilters={legendFilters}
        onLegendToggle={handleLegendToggle}
        isEditMode={isEditMode}
        onEditModeToggle={setIsEditMode}
        onEditModeCancel={handleEditModeCancel}
        onSave={handleSaveVersion}
        currentVersion={currentVersion}
        versions={versions}
        showVersionDropdown={showVersionDropdown}
        onVersionDropdownToggle={() => setShowVersionDropdown(!showVersionDropdown)}
        onVersionChange={handleVersionChange}
        onVersionSelect={handleVersionSelect}
        selectedVersions={selectedVersions}
        onBulkDeleteVersions={handleBulkDeleteVersions}
        getVersionDisplayText={getVersionDisplayText}
        showFavoriteInput={showFavoriteInput}
        favoriteAlias={favoriteAlias}
        onFavoriteInputToggle={() => setShowFavoriteInput(!showFavoriteInput)}
        onFavoriteAliasChange={setFavoriteAlias}
        onAddFavorite={handleAddFavorite}
        onRemoveFavorite={handleRemoveFavorite}
        onDeleteVersion={handleDeleteVersion}
        onOpenPersonManagement={() => setIsPanelOpen(true)}
      />

      {/* 메인 그리드 */}
      <div className="main-content">
        <SeatGrid
          persons={persons}
          positions={filteredPositions}
          onPositionChange={handlePositionChange}
          isEditMode={isEditMode}
          onPersonEdit={setEditPerson}
          onPersonDelete={handleDeletePerson}
          onPersonDuplicate={handleDuplicatePerson}
          legendFilters={legendFilters}
          handleLegendToggle={handleLegendToggle}
        />
        <StatsDisplay 
          persons={persons}
          positions={positions}
        />
      </div>
      
      {/* 인원 관리 패널 (우측) */}
      <PersonManagementPanel
        open={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        people={persons}
        onUpdatePerson={handleUpdatePerson}
        onAddPerson={handleAddPerson}
        onDeletePerson={handleDeletePersonPanel}
      />
      
      {/* 인원 정보 수정 다이얼로그 */}
      {editPerson && (
        <div 
          className="edit-dialog-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setEditPerson(null);
            }
          }}
        >
          <div className="edit-dialog">
            <h2 className="edit-dialog-title">인원 정보 수정</h2>
            <div className="edit-dialog-content">
              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">이름</label>
                  <input
                    className="form-input"
                    value={editPerson?.name || ''}
                    onChange={e => setEditPerson(editPerson ? { ...editPerson, name: e.target.value } : null)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">시작일</label>
                  <div className="date-input-wrapper">
                    <input
                      className="form-input date-input-expanded"
                      type="date"
                      value={editPerson?.startDate || ''}
                      onChange={e => setEditPerson(editPerson ? { ...editPerson, startDate: e.target.value } : null)}
                    />
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">소속</label>
                  <EditDialogDepartmentDropdown
                    value={editPerson?.department || 'pxd'}
                    onChange={(value) => setEditPerson(editPerson ? { ...editPerson, department: value, color: getColorByDepartment(value) } : null)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">종료일</label>
                  <div className="date-input-wrapper">
                    <input
                      className="form-input date-input-expanded"
                      type="date"
                      value={editPerson?.endDate || ''}
                      onChange={e => setEditPerson(editPerson ? { ...editPerson, endDate: e.target.value } : null)}
                    />
                  </div>
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">프로젝트명</label>
                  <input
                    className="form-input"
                    value={editPerson?.projectName || ''}
                    onChange={e => setEditPerson(editPerson ? { ...editPerson, projectName: e.target.value } : null)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">배치 예정</label>
                  <div 
                    className={`filter-chip ${editPerson?.isPlanned ? 'active' : ''}`}
                    onClick={() => setEditPerson(editPerson ? { ...editPerson, isPlanned: !editPerson.isPlanned } : null)}
                    style={{ cursor: 'pointer', width: 'fit-content' }}
                  >
                    <span>배치 예정</span>
                    <i className={`ri-check-line ${editPerson?.isPlanned ? 'checked' : 'unchecked'}`}></i>
                  </div>
                </div>
              </div>
              <div className="form-field">
                <label className="form-label">비고</label>
                <input
                  className="form-input"
                  value={editPerson?.usageType || ''}
                  onChange={e => setEditPerson(editPerson ? { ...editPerson, usageType: e.target.value } : null)}
                />
              </div>
            </div>
            <div className="edit-dialog-actions">
              <button className="dialog-btn dialog-btn-cancel" onClick={() => setEditPerson(null)}>
                취소
              </button>
              <button 
                className="dialog-btn dialog-btn-save" 
                onClick={async () => { 
                  await handleUpdatePerson(editPerson!); 
                  setEditPerson(null); 
                }}
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 버전 삭제 확인 다이얼로그 */}
      {deleteVersionId && (
        <div className="edit-dialog-overlay">
          <div className="edit-dialog">
            <h2 className="edit-dialog-title">정말 이 버전을 삭제하시겠습니까?</h2>
            <div className="edit-dialog-actions">
              <button className="dialog-btn dialog-btn-cancel" onClick={() => setDeleteVersionId(null)}>
                취소
              </button>
              <button 
                className="dialog-btn dialog-btn-delete" 
                onClick={handleConfirmDeleteVersion}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 저장되지 않은 변경사항 확인 다이얼로그 */}
      {showUnsavedChangesDialog && (
        <div className="edit-dialog-overlay">
          <div className="edit-dialog">
            <h2 className="edit-dialog-title">테이블과 배치 데이터가 일치하지 않습니다</h2>
            <p className="edit-dialog-message">
              테이블에 있는 인원이 지도에 배치되지 않았습니다.<br/>
              인원을 지도에 배치하고 저장하거나, 배치되지 않은 인원을 삭제할 수 있습니다.
            </p>
            <div className="edit-dialog-actions">
              <button 
                className="dialog-btn dialog-btn-cancel" 
                onClick={() => setShowUnsavedChangesDialog(false)}
              >
                계속 수정하기
              </button>
              <button 
                className="dialog-btn dialog-btn-delete" 
                onClick={handleDeleteAndExit}
              >
                인원 삭제하고 나가기
              </button>
              <button 
                className="dialog-btn dialog-btn-save" 
                onClick={handleSaveAndExit}
              >
                배치 저장하고 나가기
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default SeatArrangementPage; 